(function () {
  'use strict';

  // Animates the price element from its current value to `str` if motion.js
  // loaded; otherwise drops `str` in directly. innerHTML preserves the Transcy
  // currency wrapper in the settled state.
  function setPrice(el, str) {
    if (!el || str == null) return;
    if (window.__vextMotion && window.__vextMotion.animatePrice) {
      window.__vextMotion.animatePrice(el, str);
    } else {
      el.innerHTML = str;
    }
  }

  // ---------- Money formatting ----------
  // Any amount computed in the browser has to be rendered in the storefront's
  // own format, which is not knowable from here: the symbol and both separators
  // are locale dependent, and a currency app wraps prices in markup. So callers
  // pass a `sample` — a fixed amount rendered server-side through the same
  // `money` filter the theme uses — and the format is read back off it. Deriving
  // it beats hardcoding, and beats each feature inventing its own rules, which
  // is how a hand-rolled version on the pod page ended up emitting a dot in a
  // comma locale.
  //
  // The sample must be large enough to show grouping and carry a fractional
  // part; 1234567 minor units does both. The last separator then identifies the
  // format: two trailing digits means it is the decimal separator, three means
  // the currency has no decimal part and that character groups thousands. (A
  // zero-decimal currency would also need the /100 below dropped; this is EUR.)
  //
  // The tag strip is not belt-and-braces: this store's money format wraps
  // prices in a currency-app <span>, and results get written with textContent
  // in places, where surviving markup would render as visible text.
  function moneyFormatFromSample(sample) {
    sample = String(sample).replace(/<[^>]*>/g, '');
    var prefix = (sample.match(/^[^0-9]*/) || [''])[0];
    var suffix = (sample.match(/[^0-9]*$/) || [''])[0];
    var core = sample.slice(prefix.length, suffix.length ? -suffix.length : undefined);
    var seps = core.replace(/[0-9]/g, '');
    var last = seps.slice(-1);
    var decimal = '', thousand = '';
    if (last) {
      if (core.slice(core.lastIndexOf(last) + 1).length === 2) {
        decimal = last;
        if (seps.length > 1) thousand = seps.charAt(0);
      } else {
        thousand = last;
      }
    }
    return { prefix: prefix, suffix: suffix, decimal: decimal, thousand: thousand };
  }

  function formatMoney(cents, sample) {
    var fmt = moneyFormatFromSample(sample);
    var whole = String(Math.floor(cents / 100));
    if (fmt.thousand) whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, fmt.thousand);
    // Decimals are always shown, matching the `money` filter. Dropping them on
    // round amounts made a counting figure change shape mid-flight — "€37,50"
    // to "€40" — and the digits jump about. The only no-decimal case left is a
    // currency that genuinely has none, which the sample reports by carrying no
    // decimal separator at all.
    if (!fmt.decimal) return fmt.prefix + whole + fmt.suffix;
    var frac = cents % 100;
    return fmt.prefix + whole + fmt.decimal + (frac < 10 ? '0' + frac : String(frac)) + fmt.suffix;
  }

  // As formatMoney, minus the decimals on a round amount, like the
  // money_without_trailing_zeros filter: €97 rather than €97,00. For a label
  // that sits still, not a figure that animates.
  function formatMoneyShort(cents, sample) {
    var out = formatMoney(cents, sample);
    var fmt = moneyFormatFromSample(sample);
    var zeros = fmt.decimal + '00' + fmt.suffix;
    if (fmt.decimal && cents % 100 === 0 && out.slice(-zeros.length) === zeros) {
      out = out.slice(0, -zeros.length) + fmt.suffix;
    }
    return out;
  }

  // ---------- Announcement bar: reservation countdown ----------
  // snippets/announce-countdown. Counts down to the current stage and, at
  // zero, moves on to the next (its words, link and time), so a page cached
  // before the deadline still switches on time. After the last stage the
  // countdown goes, and the bar too if it has nothing else to show. Every
  // copy is updated each tick, as a scrolling bar clones its messages.
  (function () {
    var first = document.querySelector('[data-announce-countdown]');
    if (!first) return;
    var stages = [];
    try { stages = JSON.parse(first.getAttribute('data-stages') || '[]'); } catch (e) { return; }
    stages = stages.map(function (st) {
      return { end: Date.parse(st.t), label: st.l || '', url: st.u || '' };
    }).filter(function (st) { return !isNaN(st.end); });

    function pad(n) { return n < 10 ? '0' + n : String(n); }

    function finish() {
      document.querySelectorAll('[data-announce-countdown]').forEach(function (el) {
        var next = el.nextElementSibling;
        if (next && next.classList.contains('announce__sep')) next.parentNode.removeChild(next);
        el.parentNode.removeChild(el);
      });
      var announce = document.getElementById('announce');
      if (announce && !announce.querySelector('.announce__item')) {
        announce.style.display = 'none';
        document.documentElement.style.setProperty('--announce-h', '0px');
      }
    }

    var timer;
    function tick() {
      var now = Date.now();
      var i = 0;
      while (i < stages.length && stages[i].end <= now) i++;
      if (i >= stages.length) {
        clearInterval(timer);
        finish();
        return false;
      }
      var st = stages[i];
      var left = Math.floor((st.end - now) / 1000);
      var days = Math.floor(left / 86400);
      var time = (days > 0 ? days + 'd ' : '') + pad(Math.floor(left % 86400 / 3600)) + 'h ' +
        pad(Math.floor(left % 3600 / 60)) + 'm ' + pad(left % 60) + 's';
      document.querySelectorAll('[data-announce-countdown]').forEach(function (el) {
        var label = el.querySelector('[data-announce-countdown-label]');
        var clock = el.querySelector('[data-announce-countdown-time]');
        if (label && label.textContent !== st.label) label.textContent = st.label;
        if (st.url && el.getAttribute('href') !== st.url) el.setAttribute('href', st.url);
        if (clock) clock.textContent = time;
      });
      return true;
    }
    if (tick()) timer = setInterval(tick, 1000);
  })();

  // ---------- Announcement bar: sticky offset, marquee, dismiss ----------
  (function () {
    var announce = document.getElementById('announce');
    if (!announce) return;

    // Keep --announce-h in sync with the bar's real height so the sticky
    // header (top: var(--announce-h)) stacks directly beneath it.
    var rootStyle = document.documentElement.style;
    function syncHeight() {
      rootStyle.setProperty('--announce-h', announce.offsetHeight + 'px');
    }
    syncHeight();
    window.addEventListener('resize', syncHeight, { passive: true });

    var announceClose = document.getElementById('announce-close');
    if (announceClose) {
      announceClose.addEventListener('click', function () {
        announce.style.display = 'none';
        rootStyle.setProperty('--announce-h', '0px');
      });
    }

    // Seamless marquee: measure one message group, clone it enough times to
    // fill the bar, then animate the track by exactly one group width so the
    // loop has no visible seam. Skipped (static, centered) when scrolling is
    // off or the visitor prefers reduced motion.
    var track = announce.querySelector('[data-announce-track]');
    var group = track && track.querySelector('[data-announce-group]');
    var reduced = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!track || !group || !announce.classList.contains('announce--scroll')) return;
    if (reduced) { announce.classList.add('announce--static'); return; }

    var viewport = announce.querySelector('.announce__viewport');
    var speed = parseFloat(announce.getAttribute('data-speed')) || 60;
    if (speed <= 0) speed = 60;
    var groupHTML = group.outerHTML;

    function build() {
      track.classList.remove('is-animating');
      track.innerHTML = groupHTML;
      var first = track.firstElementChild;
      var groupW = first.getBoundingClientRect().width;
      if (!groupW) return;
      var barW = (viewport || announce).getBoundingClientRect().width || window.innerWidth;
      var copies = Math.ceil(barW / groupW) + 1;
      if (copies < 2) copies = 2;
      for (var i = 1; i < copies; i++) {
        var clone = first.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true'); // don't let AT read the dupes
        track.appendChild(clone);
      }
      track.style.setProperty('--announce-shift', '-' + groupW + 'px');
      track.style.setProperty('--announce-duration', (groupW / speed) + 's');
      track.classList.add('is-animating');
    }
    build();

    // Viewport width changes alter how many copies are needed; rebuild.
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(build, 200);
    }, { passive: true });
  })();

  // ---------- Newsletter (server-rendered success state) ----------
  var newsletterSuccess = document.querySelector('.newsletter__success');
  if (newsletterSuccess && typeof newsletterSuccess.scrollIntoView === 'function') {
    newsletterSuccess.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  // ============================================================
  //  PDP interactions
  // ============================================================

  var pdpVariantData = null;
  // Gallery state is populated by initGallery() below. Keep these declared
  // ABOVE the init block so a later `var = null` initializer further down
  // the IIFE doesn't get hoisted past initGallery and wipe the values.
  var galleryRail = null;
  var gallerySlides = [];
  var galleryDots = [];
  var pdp = document.querySelector('[data-pdp-root]');
  if (pdp) {
    initVariantData();
    initGallery();
    initTiers();
    initOptionChips();
    initAddToCart();
    initStickyBar();
    initOptCollapse();
  }

  // Pod product page (separate template)
  if (document.querySelector('[data-pod-pdp]')) initPodPdp();

  // Part / accessory / nutrient product page (separate template)
  if (document.querySelector('[data-part-pdp]')) initPartPdp();

  function initPodPdp() {
    var mainImg = document.getElementById('pod-gallery-img');
    var thumbs = document.querySelectorAll('[data-pod-pdp] .thumb');
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        thumbs.forEach(function (t) { t.classList.remove('is-active'); });
        thumb.classList.add('is-active');
        var src = thumb.getAttribute('data-full-src');
        var alt = thumb.getAttribute('data-alt');
        if (mainImg && src) {
          mainImg.removeAttribute('srcset');
          mainImg.src = src;
        }
        if (mainImg && alt) mainImg.alt = alt;
      });
    });

    var qtyInput = document.querySelector('[data-pod-qty]');
    var formQty = document.querySelector('[data-pod-qty-input]');
    var dec = document.querySelector('[data-pod-qty-decrement]');
    var inc = document.querySelector('[data-pod-qty-increment]');
    var priceEl = document.getElementById('pod-price');
    var unitPrice = priceEl ? parseInt(priceEl.getAttribute('data-unit-price'), 10) : 0;
    var moneySample = priceEl ? (priceEl.getAttribute('data-money-sample') || '') : '';

    // Clamp the quantity to a positive integer and mirror it into the hidden
    // form field. Returns the normalized quantity.
    function clampQty() {
      var n = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);
      if (qtyInput) qtyInput.value = n;
      if (formQty) formQty.value = n;
      return n;
    }

    // Run the price to the line total for the chosen quantity, through the same
    // ticker the cabinet page uses for its tiers.
    //
    // Deliberately not called on load. The server renders the price with the
    // currency app's wrapper markup intact; replacing it on init with a plain
    // computed string made it flash and then vanish once that app re-processed
    // the element. Only take the element over once the shopper touches the
    // stepper, by which point they are watching it.
    function updateTotal() {
      var n = clampQty();
      if (!priceEl || !unitPrice) return;
      setPrice(priceEl, formatMoney(unitPrice * n, moneySample));
    }

    if (dec) dec.addEventListener('click', function () {
      if (!qtyInput) return;
      qtyInput.value = Math.max(1, (parseInt(qtyInput.value, 10) || 1) - 1);
      updateTotal();
    });
    if (inc) inc.addEventListener('click', function () {
      if (!qtyInput) return;
      qtyInput.value = (parseInt(qtyInput.value, 10) || 1) + 1;
      updateTotal();
    });
    if (qtyInput) qtyInput.addEventListener('input', updateTotal);
    clampQty();

    var form = document.getElementById('pod-product-form');
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var btn = form.querySelector('button[type="submit"]');
        if (btn) btn.disabled = true;
        var fd = new FormData(form);
        var id = parseInt(fd.get('id'), 10);
        var qty = parseInt(fd.get('quantity'), 10) || 1;
        if (!id) { if (btn) btn.disabled = false; return; }
        cartAdd([{ id: id, quantity: qty }])
          .then(function () { window.location.href = '/cart'; })
          .catch(function (err) {
            alert(cartErrorMessage(err));
            if (btn) btn.disabled = false;
          });
      });
    }
  }


  // Part, accessory, and nutrient PDP. Shares the pod panel's markup for the
  // gallery, the quantity stepper, and the price ticker, and adds the one thing
  // pods never needed: a variant picker. Kept separate from the cabinet's
  // initOptionChips/syncVariantFromOptions pair because those write to the
  // cabinet's own element ids and drag the tier and kit-component machinery
  // along with them.
  function initPartPdp() {
    var root = document.querySelector('[data-part-pdp]');
    var mainImg = document.getElementById('part-gallery-img');
    var priceEl = document.getElementById('part-price');
    var idInput = document.getElementById('part-variant-id');
    var addBtn = document.getElementById('part-add-btn');
    var addLabel = addBtn ? addBtn.querySelector('.part-add-label') : null;
    var skuEl = document.querySelector('[data-part-sku]');
    var shipEl = document.querySelector('[data-part-ship]');
    var shipTextEl = document.querySelector('[data-part-ship-text]');
    var qtyInput = document.querySelector('[data-part-qty]');
    var formQty = document.querySelector('[data-part-qty-input]');
    var dec = document.querySelector('[data-part-qty-decrement]');
    var inc = document.querySelector('[data-part-qty-increment]');
    var moneySample = priceEl ? (priceEl.getAttribute('data-money-sample') || '') : '';
    // NaN rather than 0 when absent, because 0 is a legitimate unit price here —
    // a part can be listed free (a warranty replacement power supply) and
    // switching to it still has to repaint the figure. The pod panel guards on
    // falsiness instead, which it can afford: no pod pack costs nothing.
    var unitPrice = priceEl ? parseInt(priceEl.getAttribute('data-unit-price'), 10) : NaN;

    var variants = [];
    var dataEl = document.getElementById('part-variant-data');
    if (dataEl) {
      try {
        var parsed = JSON.parse(dataEl.textContent);
        variants = (parsed && parsed.variants) || [];
      } catch (e) {
        console.warn('[Vext part PDP] Failed to parse #part-variant-data JSON:', e);
      }
    }
    var variantsCarryShipNotes = variants.some(function (v) { return !!v.ship_note; });

    // ---- gallery thumbs ----
    var thumbs = root ? root.querySelectorAll('.thumb') : [];
    Array.prototype.forEach.call(thumbs, function (thumb) {
      thumb.addEventListener('click', function () {
        Array.prototype.forEach.call(thumbs, function (t) { t.classList.remove('is-active'); });
        thumb.classList.add('is-active');
        var src = thumb.getAttribute('data-full-src');
        var alt = thumb.getAttribute('data-alt');
        if (mainImg && src) crossfadeImage(mainImg, src);
        if (mainImg && alt) mainImg.alt = alt;
      });
    });

    // ---- quantity ----
    function clampQty() {
      var n = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);
      if (qtyInput) qtyInput.value = n;
      if (formQty) formQty.value = n;
      return n;
    }

    // Deliberately not called on load, for the same reason the pod panel holds
    // off: the server renders the price with the currency app's wrapper markup
    // intact, and replacing it on init made it flash and vanish once that app
    // re-processed the element. Only take the element over once the shopper
    // touches the stepper or the variant chips.
    function updateTotal() {
      var n = clampQty();
      if (!priceEl || isNaN(unitPrice)) return;
      setPrice(priceEl, formatMoney(unitPrice * n, moneySample));
    }

    if (dec) dec.addEventListener('click', function () {
      if (!qtyInput) return;
      qtyInput.value = Math.max(1, (parseInt(qtyInput.value, 10) || 1) - 1);
      updateTotal();
    });
    if (inc) inc.addEventListener('click', function () {
      if (!qtyInput) return;
      qtyInput.value = (parseInt(qtyInput.value, 10) || 1) + 1;
      updateTotal();
    });
    if (qtyInput) qtyInput.addEventListener('input', updateTotal);
    clampQty();

    // ---- variant chips ----
    function selectedValues() {
      var values = [];
      var groups = document.querySelectorAll('[data-part-chips-for]');
      Array.prototype.forEach.call(groups, function (group) {
        var pos = parseInt(group.getAttribute('data-part-chips-for'), 10);
        var active = group.querySelector('.is-active');
        if (active && pos >= 1) values[pos - 1] = active.getAttribute('data-option-value');
      });
      return values;
    }

    function syncVariant() {
      if (!variants.length) return;
      var selected = selectedValues();
      var match = null;
      for (var i = 0; i < variants.length; i++) {
        var opts = variants[i].options || [];
        var ok = true;
        for (var j = 0; j < selected.length; j++) {
          if (selected[j] != null && norm(opts[j]) !== norm(selected[j])) { ok = false; break; }
        }
        if (ok) { match = variants[i]; break; }
      }

      if (!match) {
        if (addBtn) addBtn.disabled = true;
        if (addLabel) addLabel.textContent = addBtn ? (addBtn.dataset.labelUnavailable || 'Unavailable') : 'Unavailable';
        console.warn('[Vext part PDP] No variant matches selection', selected);
        return;
      }

      if (idInput) idInput.value = match.id;
      if (addBtn) {
        addBtn.disabled = !match.available;
        if (addLabel) {
          addLabel.textContent = match.available
            ? (addBtn.dataset.labelAdd || 'Add to cart')
            : (addBtn.dataset.labelSoldOut || 'Sold out');
        }
      }
      if (skuEl) {
        skuEl.textContent = match.sku || '';
        // A variant with no SKU would otherwise leave a bare "SKU" label behind.
        var skuRow = skuEl.closest('.part-buy__sku');
        if (skuRow) skuRow.hidden = !match.sku;
      }
      // Ship window follows the selection: one option can be on the shelf while
      // another is on back-order, and because the store oversells both stay
      // buyable, so this strip is the only thing telling them apart. Liquid has
      // already resolved variant-then-product precedence into ship_note.
      //
      // Only touched when the payload carries a note for at least one variant.
      // Otherwise the strip is showing the section-level richtext fallback,
      // which is not variant-specific and must not be overwritten or hidden.
      if (shipEl && shipTextEl && variantsCarryShipNotes) {
        var note = match.ship_note || '';
        shipTextEl.textContent = note;
        shipEl.hidden = !note;
      }
      // Variants can be priced differently, so the ticker's unit has to move
      // with the selection before the quantity is applied to it.
      if (typeof match.price === 'number') {
        unitPrice = match.price;
        if (priceEl) priceEl.setAttribute('data-unit-price', String(match.price));
        updateTotal();
      }
      if (match.featured_image_url && mainImg) crossfadeImage(mainImg, match.featured_image_url);
    }

    var chipGroups = document.querySelectorAll('[data-part-chips-for]');
    Array.prototype.forEach.call(chipGroups, function (group) {
      var pos = group.getAttribute('data-part-chips-for');
      var label = document.querySelector('[data-part-selected-for="' + pos + '"]');
      var chips = group.querySelectorAll('button[data-option-value]');
      Array.prototype.forEach.call(chips, function (chip) {
        chip.addEventListener('click', function () {
          Array.prototype.forEach.call(chips, function (c) { c.classList.remove('is-active'); });
          chip.classList.add('is-active');
          var value = chip.getAttribute('data-option-value') || '';
          if (label) label.textContent = value;
          syncVariant();
        });
      });
    });

    // ---- add to cart ----
    var form = document.getElementById('part-product-form');
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var btn = form.querySelector('button[type="submit"]');
        if (btn) btn.disabled = true;
        var fd = new FormData(form);
        var id = parseInt(fd.get('id'), 10);
        var qty = parseInt(fd.get('quantity'), 10) || 1;
        if (!id) { if (btn) btn.disabled = false; return; }
        cartAdd([{ id: id, quantity: qty }])
          .then(function () { window.location.href = '/cart'; })
          .catch(function (err) {
            alert(cartErrorMessage(err));
            if (btn) btn.disabled = false;
          });
      });
    }
  }

  function initVariantData() {
    var script = document.getElementById('pdp-variant-data');
    if (!script) {
      console.warn('[Vext PDP] No #pdp-variant-data found; chips will not drive variant selection.');
      return;
    }
    try {
      pdpVariantData = JSON.parse(script.textContent);
    } catch (e) {
      pdpVariantData = null;
      console.warn('[Vext PDP] Failed to parse #pdp-variant-data JSON:', e);
      return;
    }
    if (!pdpVariantData.variants || !pdpVariantData.variants.length) {
      console.warn('[Vext PDP] No variants in #pdp-variant-data.');
    }
  }

  function norm(s) { return (s == null ? '' : String(s)).toLowerCase().trim(); }

  function getSelectedOptionValues() {
    var values = [];
    var groups = document.querySelectorAll('[data-chips-for]');
    groups.forEach(function (group) {
      var pos = parseInt(group.getAttribute('data-chips-for'), 10);
      var active = group.querySelector('.is-active');
      if (active && pos >= 1) {
        values[pos - 1] = active.getAttribute('data-option-value');
      }
    });
    return values;
  }

  function syncVariantFromOptions() {
    if (!pdpVariantData) return;
    var idInput = document.getElementById('pdp-variant-id');
    var addBtn = document.getElementById('pdp-add-btn');
    var addLabel = addBtn ? addBtn.querySelector('.pdp-add-label') : null;
    // No add button while the cabinet page shows its order countdown; the
    // colour chips still swap the gallery image.
    if (!idInput) return;

    var selected = getSelectedOptionValues();
    var match = null;
    for (var i = 0; i < pdpVariantData.variants.length; i++) {
      var v = pdpVariantData.variants[i];
      var optsDc = v.options_dc || (v.options || []).map(norm);
      var ok = true;
      for (var j = 0; j < selected.length; j++) {
        if (selected[j] != null && optsDc[j] !== norm(selected[j])) {
          ok = false;
          break;
        }
      }
      if (ok) { match = v; break; }
    }

    // data-keep-enabled: in reservation mode the button is the email sign-up,
    // which never depends on a variant being in stock.
    var keepEnabled = !addBtn || addBtn.hasAttribute('data-keep-enabled');
    if (match) {
      idInput.value = match.id;
      if (!keepEnabled) addBtn.disabled = !match.available;
      if (addLabel) {
        addLabel.textContent = match.available
          ? (addBtn.dataset.labelAdd || 'Add to cart')
          : (addBtn.dataset.labelSoldOut || 'Sold out');
      }
      if (match.featured_image_url) {
        var galleryImg = document.getElementById('pdp-gallery-img');
        crossfadeImage(galleryImg, match.featured_image_url);
        var firstSlideImg = galleryRail && galleryRail.querySelector('.gallery__slide img');
        crossfadeImage(firstSlideImg, match.featured_image_url);
        if (galleryRail) galleryRail.scrollTo({ left: 0, behavior: 'smooth' });
      }
    } else {
      if (!keepEnabled) addBtn.disabled = true;
      if (addLabel) addLabel.textContent = addBtn.dataset.labelUnavailable || 'Unavailable';
      console.warn('[Vext PDP] No variant matches selection', selected);
    }
  }


  // Crossfade an <img> to a new src by layering a new <img> on top as an
  // overlay, fading IT in to opacity 1, then swapping the underlying src
  // and removing the overlay. No visible blink — the user sees the new
  // image build up over the old one.
  function crossfadeImage(img, newSrc) {
    if (!img || !newSrc || img.src === newSrc) return;
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      img.removeAttribute('srcset');
      img.src = newSrc;
      return;
    }
    var parent = img.parentNode;
    if (!parent) return;
    // Discard any in-flight overlay from a rapid prior click on this img.
    var existing = parent.querySelector(':scope > [data-crossfade-overlay]');
    if (existing) parent.removeChild(existing);
    if (getComputedStyle(parent).position === 'static') {
      parent.style.position = 'relative';
    }
    var overlay = document.createElement('img');
    overlay.setAttribute('data-crossfade-overlay', '');
    overlay.alt = '';
    overlay.decoding = 'async';
    overlay.style.cssText =
      'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;' +
      'opacity:0;transition:opacity 280ms ease;pointer-events:none;';
    overlay.src = newSrc;
    parent.appendChild(overlay);
    function fadeIn() {
      requestAnimationFrame(function () { overlay.style.opacity = '1'; });
    }
    // Cached images don't fire onload; check .complete first.
    if (overlay.complete) fadeIn();
    else {
      overlay.addEventListener('load', fadeIn);
      overlay.addEventListener('error', fadeIn);
    }
    // Safety net + swap-and-cleanup after the fade has finished.
    setTimeout(function () {
      img.removeAttribute('srcset');
      img.src = newSrc;
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    }, 320);
  }

  function initGallery() {
    var galleryImg = document.getElementById('pdp-gallery-img');
    var galleryCaption = document.getElementById('pdp-gallery-caption');
    var thumbs = document.querySelectorAll('[data-gallery] .thumb');

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        thumbs.forEach(function (t) { t.classList.remove('is-active'); });
        thumb.classList.add('is-active');
        var src = thumb.getAttribute('data-full-src');
        var caption = thumb.getAttribute('data-caption');
        var alt = thumb.getAttribute('data-alt');
        if (src && galleryImg) crossfadeImage(galleryImg, src);
        if (alt && galleryImg) galleryImg.alt = alt;
        if (galleryCaption && caption) galleryCaption.textContent = caption;
      });
    });

    galleryRail = document.querySelector('[data-gallery-rail]');
    gallerySlides = galleryRail ? Array.prototype.slice.call(galleryRail.querySelectorAll('.gallery__slide')) : [];
    galleryDots = Array.prototype.slice.call(document.querySelectorAll('[data-gallery-dots] .gallery__dot'));

    // Hide slides (and their dots) whose image is some variant's featured
    // image. Slide 0 stays visible because that's where we crossfade the
    // picked variant; everything else duplicates if we leave it in.
    hideDuplicateVariantSlides();

    if (galleryRail && galleryDots.length) {
      function setActiveDot(i) {
        galleryDots.forEach(function (d, di) { d.classList.toggle('is-active', di === i); });
      }

      // Dot click → scroll the rail to that slide.
      galleryDots.forEach(function (dot) {
        dot.addEventListener('click', function () {
          var i = parseInt(dot.getAttribute('data-dot-index'), 10) || 0;
          // Fall back to a fresh DOM lookup if the cache misses for any reason.
          var slide = gallerySlides[i]
            || document.querySelector('[data-gallery-rail] .gallery__slide[data-slide-index="' + i + '"]');
          if (slide && slide.style.display !== 'none') {
            galleryRail.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
          }
        });
      });

      // Active-dot tracking. Read scrollLeft directly (cheaper than
      // getBoundingClientRect on every slide) and map it through the
      // visible-slide list so hidden slides don't throw off the index.
      function updateActiveDot() {
        // Refresh the slide cache if it's empty — defends against initGallery
        // running before the slides were in DOM, or a re-render replacing them.
        if (!gallerySlides.length && galleryRail) {
          gallerySlides = Array.prototype.slice.call(galleryRail.querySelectorAll('.gallery__slide'));
        }
        var visible = [];
        for (var i = 0; i < gallerySlides.length; i++) {
          if (gallerySlides[i].style.display !== 'none') visible.push(i);
        }
        if (!visible.length) return;
        var w = galleryRail.clientWidth;
        if (!w) return;
        var slot = Math.round(galleryRail.scrollLeft / w);
        if (slot < 0) slot = 0;
        if (slot >= visible.length) slot = visible.length - 1;
        setActiveDot(visible[slot]);
      }

      // Belt-and-braces event coverage. iOS scroll-snap rails sometimes
      // skip scroll events during the snap; touchend + a polling interval
      // catch every case the events miss.
      galleryRail.addEventListener('scroll', updateActiveDot, { passive: true });
      galleryRail.addEventListener('scrollend', updateActiveDot, { passive: true });
      galleryRail.addEventListener('touchend', updateActiveDot, { passive: true });

      var pollLast = -1;
      setInterval(function () {
        if (!galleryRail) return;
        if (galleryRail.scrollLeft === pollLast) return;
        pollLast = galleryRail.scrollLeft;
        updateActiveDot();
      }, 120);

      updateActiveDot();
    }
  }

  // Normalize a Shopify CDN URL so different widths / cache busters compare equal.
  function normalizeImageUrl(url) {
    return String(url || '')
      .replace(/^https?:/, '')
      .replace(/_\d+x\d*(?=\.[a-z]+)/i, '')
      .split('?')[0];
  }

  function hideDuplicateVariantSlides() {
    if (!pdpVariantData || !pdpVariantData.variants || !gallerySlides.length) return;
    var variantUrls = Object.create(null);
    pdpVariantData.variants.forEach(function (v) {
      if (v.featured_image_url) variantUrls[normalizeImageUrl(v.featured_image_url)] = true;
    });
    gallerySlides.forEach(function (slide, i) {
      if (i === 0) return;
      var url = normalizeImageUrl(slide.getAttribute('data-image-url'));
      if (variantUrls[url]) {
        slide.style.display = 'none';
        if (galleryDots[i]) galleryDots[i].style.display = 'none';
      }
    });
  }

  var collapseAllTouched = function () {};  // set by initOptCollapse below

  // Mobile-only progressive collapse for the buy panel selectors.
  // Rule: a selector is "touched" the first time the user picks an option
  // inside it. Whenever the user picks an option in group X, every OTHER
  // touched group collapses — so the previously-touched selector tightens up
  // as the user moves through the flow. The just-touched group stays open.
  // Manual .opt-head toggle still works as an override.
  function initOptCollapse() {
    var groups = document.querySelectorAll('[data-opt-group]');
    if (!groups.length) return;
    var isMobile = function () {
      return window.matchMedia('(max-width: 1024px)').matches;
    };
    function setCollapsed(group, collapsed) {
      group.classList.toggle('is-collapsed', !!collapsed);
      var head = group.querySelector('[data-opt-toggle]');
      if (head) head.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
    }
    var touched = Object.create(null);
    // Some selectors (currently just plug, marked with data-opt-plug) auto-
    // collapse 1s after a pick — they start collapsed and tighten back up
    // once the user has confirmed a value. Tier and color don't; their
    // collapse is driven by the user picking in the next group.
    var autoCollapseTimer = null;
    var autoCollapseGroup = null;
    function scheduleAutoCollapse(group) {
      if (!group.hasAttribute('data-opt-plug')) return;
      if (autoCollapseTimer) clearTimeout(autoCollapseTimer);
      autoCollapseGroup = group;
      autoCollapseTimer = setTimeout(function () {
        if (autoCollapseGroup && !autoCollapseGroup.classList.contains('is-collapsed')) {
          setCollapsed(autoCollapseGroup, true);
        }
        autoCollapseTimer = null;
        autoCollapseGroup = null;
      }, 1000);
    }
    function cancelAutoCollapse() {
      if (autoCollapseTimer) clearTimeout(autoCollapseTimer);
      autoCollapseTimer = null;
      autoCollapseGroup = null;
    }
    function markTouchedAndCollapseOthers(thisId, thisGroup) {
      touched[thisId] = true;
      Array.prototype.forEach.call(groups, function (g) {
        var id = g.getAttribute('data-opt-group');
        if (id === thisId) {
          // Just-touched group: make sure it's expanded.
          if (g.classList.contains('is-collapsed')) setCollapsed(g, false);
        } else if (touched[id]) {
          // Other previously-touched group: collapse it.
          if (!g.classList.contains('is-collapsed')) setCollapsed(g, true);
        }
      });
      scheduleAutoCollapse(thisGroup);
    }
    // Exposed for the sticky bar so scrolling past the buy panel collapses
    // every touched selector (including the last-touched one).
    collapseAllTouched = function () {
      if (!isMobile()) return;
      cancelAutoCollapse();
      Array.prototype.forEach.call(groups, function (g) {
        var id = g.getAttribute('data-opt-group');
        if (touched[id] && !g.classList.contains('is-collapsed')) setCollapsed(g, true);
      });
    };

    Array.prototype.forEach.call(groups, function (group) {
      var id = group.getAttribute('data-opt-group');
      var head = group.querySelector('[data-opt-toggle]');
      if (head) {
        head.addEventListener('click', function () {
          if (!isMobile()) return;
          setCollapsed(group, !group.classList.contains('is-collapsed'));
          // Manual toggle = explicit user intent; cancel any pending
          // auto-collapse so the group doesn't snap shut a moment later.
          cancelAutoCollapse();
        });
        head.addEventListener('keydown', function (e) {
          if (!isMobile()) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setCollapsed(group, !group.classList.contains('is-collapsed'));
          }
        });
      }
      // Picking an option (tier card or chip) marks this group as touched
      // and collapses any other group the user has already touched. To keep
      // the tapped element pinned under the user's finger, predict the
      // shrink of any group ABOVE the tap that's about to collapse, and
      // kick off one smooth scrollBy in parallel with the collapse — the
      // browser animates the scroll over the same ~300ms the height
      // animates, so the chip stays put without per-frame layout work.
      group.addEventListener('click', function (e) {
        if (!isMobile()) return;
        var pick = e.target.closest('.tier, .chip-color, .chip-text');
        if (!pick) return;
        var pickTop = pick.getBoundingClientRect().top;
        var predicted = 0;
        Array.prototype.forEach.call(groups, function (g) {
          var gid = g.getAttribute('data-opt-group');
          if (gid === id) return;
          if (!touched[gid]) return;
          if (g.classList.contains('is-collapsed')) return;
          var gRect = g.getBoundingClientRect();
          if (gRect.bottom > pickTop) return;
          var headEl = g.querySelector('[data-opt-toggle]');
          var headH = headEl ? headEl.offsetHeight : 44;
          var collapsedH;
          if (gid === 'tier') {
            var activeTier = g.querySelector('.tier.is-active');
            collapsedH = headH + (activeTier ? activeTier.offsetHeight : 80);
          } else {
            collapsedH = headH + 44;
          }
          predicted -= Math.max(0, g.offsetHeight - collapsedH);
        });
        markTouchedAndCollapseOthers(id, group);
        if (Math.abs(predicted) > 4) {
          // Drive scroll alongside the collapse animation with an rAF easing
          // curve matched to the CSS transition (~280ms easeOutCubic). One
          // scrollTo write per frame, no layout reads — keeps the tapped
          // chip stable without the rect-reading overhead of the old anchor.
          var startY = window.scrollY;
          var t0 = performance.now();
          var DUR = 280;
          (function step() {
            var t = Math.min(1, (performance.now() - t0) / DUR);
            var eased = 1 - Math.pow(1 - t, 3);
            window.scrollTo(0, startY + predicted * eased);
            if (t < 1) requestAnimationFrame(step);
          })();
        }
      });
      // Resize back to desktop: ensure the group is expanded.
      window.addEventListener('resize', function () {
        if (!isMobile() && group.classList.contains('is-collapsed')) setCollapsed(group, false);
      });
    });
  }

  // Sticky mobile buy bar — fade in as soon as the Add-to-Cart button
  // scrolls above the top third of the viewport, and fade back out when
  // it's visible again (or hasn't been reached yet).
  // Tap on the bar (excluding the CTA / interactive controls) smoothly
  // scrolls back to the buy panel.
  function initStickyBar() {
    var bar = document.querySelector('.mobile-buy');
    var panel = document.querySelector('.buy-panel');
    var cta = document.getElementById('pdp-add-btn') || (panel && panel.querySelector('button[type="submit"]'));
    if (!bar || !panel) return;
    if ('IntersectionObserver' in window && cta) {
      // rootMargin shrinks the bottom 66% of the viewport, so the effective
      // intersection zone is only the top third. When the CTA leaves that
      // zone going UP (rect.top < 0), we show the bar; if it leaves going
      // DOWN (still below the trigger line), the bar stays hidden.
      var wasOn = false;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          var shouldBeOn;
          if (e.isIntersecting) {
            shouldBeOn = false;
          } else if (e.boundingClientRect.top < 0) {
            shouldBeOn = true;
          } else {
            shouldBeOn = false;
          }
          bar.classList.toggle('is-on', shouldBeOn);
          // First time the bar comes on, collapse any selectors that are
          // still expanded — the user has moved on past the buy panel.
          if (shouldBeOn && !wasOn) collapseAllTouched();
          wasOn = shouldBeOn;
        });
      }, { threshold: 0, rootMargin: '0px 0px -66% 0px' });
      io.observe(cta);
    } else if (!cta) {
      bar.classList.add('is-on');
    }
    bar.addEventListener('click', function (e) {
      // Let buttons / links / inputs handle their own clicks (Add to cart, etc.)
      if (e.target.closest('button, a, input, [role="button"]')) return;
      panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  function initTiers() {
    var tiers = document.querySelectorAll('[data-tier-rows] .tier');
    var price = document.getElementById('pdp-price');
    var save = document.getElementById('pdp-save');
    var tierName = document.getElementById('pdp-tier-name');
    var mobilePrice = document.getElementById('pdp-mobile-price');
    var mobileTier = document.getElementById('pdp-mobile-tier');
    if (!tiers.length) return;

    tiers.forEach(function (tier) {
      tier.addEventListener('click', function () {
        tiers.forEach(function (t) { t.classList.remove('is-active'); });
        tier.classList.add('is-active');

        var p = tier.getAttribute('data-tier-price') || '';
        var s = tier.getAttribute('data-tier-save') || '';
        var n = tier.getAttribute('data-tier-name') || '';
        var sn = tier.getAttribute('data-tier-short') || n;

        setPrice(price, p);
        setPrice(mobilePrice, p);
        if (tierName) tierName.textContent = n;
        if (mobileTier) mobileTier.textContent = sn;
        if (save) {
          if (s) {
            save.textContent = s;
            save.style.display = '';
          } else {
            save.style.display = 'none';
          }
        }
      });
    });

    initTierDiscounts(tiers, price, mobilePrice, save);
  }

  // ---------- Tier prices after automatic discounts ----------
  // Liquid can't see automatic discounts, so a tier's price on the page is the
  // cabinet plus the membership at list price. The cart knows better: buying
  // them together triggers the bundle discount. For each tier with a
  // membership, price a throwaway cart through the Storefront API (no token
  // needed for carts) holding the cabinet and the membership, and show what it
  // charges. The visitor's own cart is never touched. Results are kept for ten
  // minutes per tab so moving between pages doesn't refetch.
  //
  // For a logged-in customer the cart carries their email, so discounts meant
  // only for them (a customer segment, e.g. reservation holders by tag) count
  // too; their pages aren't shared between visitors, so the email is only
  // ever on their own page. If the cabinet can't be added (sold out), the
  // cart drops it, and the list price stays.
  //
  // Constants live inside the functions: initTiers() runs from the init block
  // above, before any top-level `var` down here has been assigned.
  function initTierDiscounts(tiers, price, mobilePrice, save) {
    var CACHE_KEY = 'vextTierPrice:';
    var CACHE_MS = 10 * 60 * 1000;
    var rows = document.querySelector('[data-tier-rows]');
    var dataEl = document.getElementById('pdp-variant-data');
    if (!rows || !dataEl || !window.fetch) return;
    var sample = rows.getAttribute('data-money-sample') || '';
    var country = rows.getAttribute('data-country') || '';
    var currency = rows.getAttribute('data-currency') || '';
    var buyer = { countryCode: country, email: rows.getAttribute('data-customer-email') || '' };
    var customerId = rows.getAttribute('data-customer') || '';
    // Free add-on (main-product "Free add-on"): priced along with each tier,
    // and offered when the cart makes it free for this visitor.
    var addonId = rows.getAttribute('data-addon-variant') || '';
    var addonValue = parseInt(rows.getAttribute('data-addon-value'), 10) || 0;

    var cabinetId = null;
    try {
      var variants = JSON.parse(dataEl.textContent).variants || [];
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].available) { cabinetId = variants[i].id; break; }
      }
    } catch (e) { return; }
    if (!cabinetId) return;

    Array.prototype.forEach.call(tiers, function (tier) {
      var membershipId = tier.getAttribute('data-membership-variant') || '';
      var listCents = parseInt(tier.getAttribute('data-tier-cents'), 10);
      if (!(listCents > 0) || (!membershipId && !addonId)) return;

      var key = CACHE_KEY + [cabinetId, membershipId, addonId, country, customerId].join(':');
      var cached = null;
      try { cached = JSON.parse(sessionStorage.getItem(key) || 'null'); } catch (e) { /* storage blocked */ }
      if (cached && Date.now() - cached.at < CACHE_MS) {
        applyTier(tier, cached);
        return;
      }

      var ids = [cabinetId];
      if (membershipId) ids.push(membershipId);
      fetchBundle(ids, addonId, buyer, currency).then(function (result) {
        if (!result) return;
        result.at = Date.now();
        try { sessionStorage.setItem(key, JSON.stringify(result)); } catch (e) { /* storage blocked */ }
        applyTier(tier, result);
      });
    });

    // Pop-up telling a visitor the add-on comes free for them (main-product
    // "Pop-up when it's free"): once per visit, closed by its button or after
    // a while on its own.
    var toastDone = false;
    function showAddonToast() {
      if (toastDone) return;
      toastDone = true;
      var toast = document.querySelector('[data-addon-toast]');
      if (!toast) return;
      try {
        if (sessionStorage.getItem('vextAddonToastShown')) return;
        sessionStorage.setItem('vextAddonToastShown', '1');
      } catch (e) { /* storage blocked: show it, once for this page */ }
      function hide() { toast.classList.remove('is-on'); }
      var close = toast.querySelector('[data-addon-toast-close]');
      if (close) close.addEventListener('click', hide);
      setTimeout(function () { toast.classList.add('is-on'); }, 600);
      setTimeout(hide, 15000);
    }

    // result: { cents, addonFree } — what the cart charges for the tier, with
    // the add-on in it when that's free.
    function applyTier(tier, result) {
      var cents = result.cents;
      var listCents = parseInt(tier.getAttribute('data-tier-cents'), 10);
      if (!(cents > 0)) return;
      tier.setAttribute('data-tier-addon', result.addonFree ? '1' : '0');
      var note = tier.querySelector('[data-tier-addon-note]');
      if (note) note.hidden = !result.addonFree;
      if (result.addonFree) showAddonToast();

      // Crossed-off price: the tier's own, or its list price when it has
      // none, plus the add-on's value when that comes free.
      var compareCents = parseInt(tier.getAttribute('data-tier-compare-cents'), 10) || 0;
      if (result.addonFree) compareCents = (compareCents || listCents) + addonValue;
      var compareEl = tier.querySelector('[data-tier-compare-el]');
      if (compareEl) {
        var showCompare = compareCents > cents;
        if (showCompare) compareEl.textContent = formatMoneyShort(compareCents, sample);
        compareEl.parentNode.hidden = !showCompare;
      }

      var formatted = cents === listCents ? tier.getAttribute('data-tier-price') : formatMoney(cents, sample);
      tier.setAttribute('data-tier-price', formatted);
      var el = tier.querySelector('[data-tier-price-el]');
      if (el) el.innerHTML = formatted;

      // The Save badge's [amount] is the crossed-off price minus this price.
      var label = tier.getAttribute('data-tier-save-label') || '';
      var saveText = tier.getAttribute('data-tier-save') || '';
      if (label.indexOf('[amount]') !== -1 && compareCents > cents) {
        saveText = label.split('[amount]').join(formatMoneyShort(compareCents - cents, sample));
        tier.setAttribute('data-tier-save', saveText);
      }
      if (tier.classList.contains('is-active')) {
        setPrice(price, formatted);
        setPrice(mobilePrice, formatted);
        if (save && saveText) {
          save.textContent = saveText;
          save.style.display = '';
        }
      }
    }
  }

  // Prices a throwaway cart with one of each variant, plus the add-on when
  // given. Resolves { cents, addonFree }: what the cart charges, the add-on
  // left in only when it comes out free; or null when it can't be priced (an
  // error, a line the cart dropped or cut down, or a currency other than the
  // page's, whose amounts wouldn't compare). An add-on the cart drops (sold
  // out, unpublished) just counts as not free.
  function fetchBundle(variantIds, addonId, buyer, currency) {
    var ids = addonId ? variantIds.concat([addonId]) : variantIds;
    var input = {
      lines: ids.map(function (id) {
        return { merchandiseId: 'gid://shopify/ProductVariant/' + id, quantity: 1 };
      })
    };
    var identity = {};
    if (buyer.countryCode) identity.countryCode = buyer.countryCode;
    if (buyer.email) identity.email = buyer.email;
    if (identity.countryCode || identity.email) input.buyerIdentity = identity;
    return fetch('/api/2026-10/graphql.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: 'mutation ($input: CartInput!) { cartCreate(input: $input) { cart { cost { totalAmount { amount currencyCode } } lines(first: 10) { nodes { quantity cost { totalAmount { amount } } merchandise { ... on ProductVariant { id } } } } } } }',
        variables: { input: input }
      })
    }).then(function (res) { return res.ok ? res.json() : null; }).then(function (json) {
      var cart = json && json.data && json.data.cartCreate && json.data.cartCreate.cart;
      if (!cart) return null;
      if (currency && cart.cost.totalAmount.currencyCode !== currency) return null;
      var cents = Math.round(parseFloat(cart.cost.totalAmount.amount) * 100);
      var addonGid = addonId ? 'gid://shopify/ProductVariant/' + addonId : '';
      var addonFree = false;
      var found = 0;
      var lines = cart.lines.nodes;
      for (var i = 0; i < lines.length; i++) {
        var line = lines[i];
        var lineCents = Math.round(parseFloat(line.cost.totalAmount.amount) * 100);
        if (line.merchandise && line.merchandise.id === addonGid) {
          if (line.quantity === 1 && lineCents === 0) addonFree = true;
          else cents -= lineCents;
        } else {
          if (line.quantity !== 1) return null;
          found++;
        }
      }
      if (found !== variantIds.length) return null;
      return { cents: cents, addonFree: addonFree };
    }).catch(function () { return null; });
  }

  function initOptionChips() {
    var groups = document.querySelectorAll('[data-chips-for]');
    if (!groups.length) return;

    groups.forEach(function (group) {
      var pos = group.getAttribute('data-chips-for');
      var label = document.querySelector('[data-selected-for="' + pos + '"]');
      var chips = group.querySelectorAll('button[data-option-value]');

      chips.forEach(function (chip) {
        chip.addEventListener('click', function () {
          chips.forEach(function (c) { c.classList.remove('is-active'); });
          chip.classList.add('is-active');
          var value = chip.getAttribute('data-option-value') || '';
          if (label) label.textContent = value;
          syncVariantFromOptions();
        });
      });
    });
  }

  // ---------- PDP add-to-cart (cabinet + optional membership + kit components) ----------
  function getActiveOptionValue(optionName) {
    if (!optionName) return '';
    var target = norm(optionName);
    var heads = document.querySelectorAll('.opt-head');
    for (var i = 0; i < heads.length; i++) {
      var labelEl = heads[i].querySelector('.opt-head__label');
      var selEl = heads[i].querySelector('[data-selected-for]');
      if (labelEl && selEl && norm(labelEl.textContent) === target) {
        return selEl.textContent || '';
      }
    }
    return '';
  }

  function collectKitItems(parentHandle) {
    var nodes = document.querySelectorAll('[data-kit-component]');
    var items = [];
    nodes.forEach(function (node) {
      var qty = parseInt(node.getAttribute('data-quantity'), 10);
      if (!qty || qty < 1) qty = 1;
      var variantId = 0;
      if (node.getAttribute('data-option-aware') === '1') {
        var optName = node.getAttribute('data-option-name') || '';
        var selected = norm(getActiveOptionValue(optName));
        var matchA = norm(node.getAttribute('data-match-a'));
        var matchB = norm(node.getAttribute('data-match-b'));
        if (selected && selected === matchA) {
          variantId = parseInt(node.getAttribute('data-variant-a'), 10);
        } else if (selected && selected === matchB) {
          variantId = parseInt(node.getAttribute('data-variant-b'), 10);
        }
      } else {
        variantId = parseInt(node.getAttribute('data-variant-id'), 10);
      }
      if (variantId) {
        items.push({
          id: variantId,
          quantity: qty,
          properties: { _kit_parent: parentHandle || '1' }
        });
      }
    });
    return items;
  }

  function initAddToCart() {
    var form = document.getElementById('pdp-product-form');
    if (!form) return;

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var btn = form.querySelector('button[type="submit"]');
      if (btn) btn.disabled = true;

      var fd = new FormData(form);
      var mainId = parseInt(fd.get('id'), 10);
      if (!mainId) {
        if (btn) btn.disabled = false;
        return;
      }

      var items = [{ id: mainId, quantity: 1 }];

      // Membership rides along as a separate cart line item
      var activeTier = document.querySelector('[data-tier-rows] .tier.is-active');
      if (activeTier) {
        var membershipId = parseInt(activeTier.getAttribute('data-membership-variant'), 10);
        if (membershipId) {
          items.push({ id: membershipId, quantity: 1 });
        }
      }

      // Free add-on, when the cart priced it free for this tier. _free_addon
      // lets the cart page take it back out if it stops being free.
      var tierRows = document.querySelector('[data-tier-rows]');
      var addonId = tierRows ? parseInt(tierRows.getAttribute('data-addon-variant'), 10) : 0;
      if (activeTier && addonId && activeTier.getAttribute('data-tier-addon') === '1') {
        items.push({ id: addonId, quantity: 1, properties: { _free_addon: '1' } });
      }

      // Kit components: hidden line items for 3PL fulfillment
      var kitWrap = document.querySelector('[data-kit-components]');
      if (kitWrap) {
        var parentHandle = kitWrap.getAttribute('data-kit-parent') || '';
        items = items.concat(collectKitItems(parentHandle));
      }

      cartAdd(items)
        .then(function () { window.location.href = '/cart'; })
        .catch(function (err) {
          alert(cartErrorMessage(err, 'Could not add to cart. Please try again.'));
          if (btn) btn.disabled = false;
        });
    });
  }

  // ---------- FAQ accordion (PDP + FAQ page) — per-list scope ----------
  document.querySelectorAll('[data-faq-list]').forEach(function (list) {
    var items = list.querySelectorAll('[data-faq-item]');
    items.forEach(function (item) {
      var btn = item.querySelector('[data-faq-toggle]');
      if (!btn) return;
      btn.addEventListener('click', function () {
        var willOpen = !item.classList.contains('is-open');
        items.forEach(function (other) {
          other.classList.remove('is-open');
          var ob = other.querySelector('[data-faq-toggle]');
          if (ob) ob.setAttribute('aria-expanded', 'false');
        });
        if (willOpen) {
          item.classList.add('is-open');
          btn.setAttribute('aria-expanded', 'true');
        }
      });
    });
  });

  // ---------- Spec sheet accordion (PDP) ----------
  // Multi-open, unlike the FAQ: it's a reference document, and opening one
  // group shouldn't close the one the customer is comparing against.
  document.querySelectorAll('[data-spec-acc]').forEach(function (acc) {
    acc.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-spec-toggle]');
      if (!btn) return;
      var group = btn.closest('[data-spec-group]');
      if (!group) return;
      var open = group.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  });

  // ---------- Mobile-only item accordions (how-app, how-you-handle) ----------
  // Each item collapses under its own header at <=900px. Desktop keeps the
  // full grid (the collapse CSS is scoped to the breakpoint), so we ignore
  // clicks there and leave aria-expanded reflecting the mobile state.
  document.querySelectorAll('[data-collapse-acc]').forEach(function (acc) {
    acc.addEventListener('click', function (e) {
      if (!window.matchMedia('(max-width: 900px)').matches) return;
      var btn = e.target.closest('[data-collapse-toggle]');
      if (!btn) return;
      var item = btn.closest('[data-collapse-item]');
      if (!item) return;
      var open = item.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  });

  // ============================================================
  //  FAQ page: search + filter + highlight + scroll-spy
  // ============================================================
  var faqSearchInput = document.getElementById('faq-search-input');
  if (faqSearchInput) initFaqPage();

  function initFaqPage() {
    // Cache original Q/A text on each item
    document.querySelectorAll('[data-faq-item]').forEach(function (item) {
      var qEl = item.querySelector('[data-faq-q]');
      var aEl = item.querySelector('[data-faq-a]');
      if (qEl) item.dataset.qOriginal = qEl.textContent;
      if (aEl) item.dataset.aOriginal = aEl.textContent;
    });

    var clearBtn = document.getElementById('faq-search-clear');
    var emptyClearBtn = document.getElementById('faq-empty-clear');
    var emptySection = document.querySelector('[data-faq-empty-state]');
    var emptyQueryEl = emptySection ? emptySection.querySelector('[data-faq-empty-query]') : null;

    faqSearchInput.addEventListener('input', applyFaqSearch);
    if (clearBtn) clearBtn.addEventListener('click', function () {
      faqSearchInput.value = '';
      applyFaqSearch();
      faqSearchInput.focus();
    });
    if (emptyClearBtn) emptyClearBtn.addEventListener('click', function () {
      faqSearchInput.value = '';
      applyFaqSearch();
      faqSearchInput.focus();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });

    function applyFaqSearch() {
      var q = faqSearchInput.value.trim();
      var qLower = q.toLowerCase();
      var hasQuery = q !== '';
      var totalMatches = 0;
      var perCatCounts = {};

      document.querySelectorAll('[data-faq-category]').forEach(function (cat) {
        var catId = cat.getAttribute('data-faq-cat-id');
        var items = cat.querySelectorAll('[data-faq-item]');
        var catMatches = 0;

        items.forEach(function (item) {
          var qOrig = item.dataset.qOriginal || '';
          var aOrig = item.dataset.aOriginal || '';
          var match = !hasQuery ||
            qOrig.toLowerCase().indexOf(qLower) !== -1 ||
            aOrig.toLowerCase().indexOf(qLower) !== -1;

          if (match) {
            item.hidden = false;
            catMatches++;
            var qEl = item.querySelector('[data-faq-q]');
            // The answer is rich text (HTML) — never rewrite its content.
            // Using textContent/highlight here flattened its paragraphs and
            // links (this ran on load with an empty query, so answers showed
            // as plain text). Search still matches the answer via aOrig (its
            // captured plain text); only the question gets the visual highlight.
            if (qEl) {
              if (hasQuery) { highlightInto(qEl, qOrig, q); }
              else { qEl.textContent = qOrig; }
            }
          } else {
            item.hidden = true;
          }
        });

        perCatCounts[catId] = catMatches;
        totalMatches += catMatches;

        cat.dataset.faqHidden = (hasQuery && catMatches === 0) ? 'true' : 'false';

        var countEl = cat.querySelector('[data-faq-cat-count]');
        if (countEl) {
          countEl.textContent = catMatches + ' ' + (catMatches === 1 ? 'question' : 'questions');
        }
      });

      document.querySelectorAll('[data-faq-chip]').forEach(function (chip) {
        var target = chip.getAttribute('data-faq-chip-target');
        var count = perCatCounts[target] || 0;
        var countEl = chip.querySelector('[data-faq-chip-count]');
        if (countEl) countEl.textContent = count;
        chip.classList.toggle('is-searching', hasQuery);
        chip.classList.toggle('is-dim', hasQuery && count === 0);
      });

      if (emptySection) {
        var showEmpty = hasQuery && totalMatches === 0;
        emptySection.dataset.faqHidden = showEmpty ? 'false' : 'true';
        if (emptyQueryEl) emptyQueryEl.textContent = q;
      }
    }

    applyFaqSearch();

    // Chip click → smooth scroll + auto-open first visible question
    document.querySelectorAll('[data-faq-chip]').forEach(function (chip) {
      chip.addEventListener('click', function (e) {
        var target = chip.getAttribute('data-faq-chip-target');
        var section = target ? document.getElementById(target) : null;
        if (!section) return;
        e.preventDefault();
        // Mark the tapped chip active immediately, so selection feels
        // responsive instead of waiting for the scroll-spy to catch up.
        document.querySelectorAll('[data-faq-chip]').forEach(function (c) {
          c.classList.toggle('is-active', c === chip);
        });
        ensureChipVisible(chip);
        history.replaceState(null, '', '#' + target);
        scrollToCategory(section);
      });
    });

    // Direct anchor link on load
    var hash = (window.location.hash || '').replace('#', '');
    if (hash) {
      var section = document.getElementById(hash);
      if (section && section.matches('[data-faq-category]')) {
        setTimeout(function () {
          scrollToCategory(section);
        }, 200);
      }
    }

    function openFirstVisibleInCategory(section) {
      var firstItem = section.querySelector('[data-faq-item]:not([hidden])');
      if (!firstItem || firstItem.classList.contains('is-open')) return;
      var btn = firstItem.querySelector('[data-faq-toggle]');
      if (btn) btn.click();
    }

    // Jump to a category, landing its heading just below the sticky bar. The
    // first answer auto-opens (max-height animates over 400ms) and the header
    // can auto-hide mid-scroll — both shift layout/offset AFTER the initial
    // scrollTo, so for a category low on the page the first scroll clamps short
    // (the "tap again finishes it" symptom). So: open, scroll, then re-assert
    // once the animation settles — unless we're already there (don't fight a
    // user who scrolled in the meantime).
    var faqScrollTimer = null;
    function scrollToCategory(section) {
      openFirstVisibleInCategory(section);
      var go = function () {
        var top = section.getBoundingClientRect().top + window.scrollY - faqScrollOffset();
        window.scrollTo({ top: top, behavior: 'smooth' });
      };
      go();
      // A fresh jump cancels the previous pending re-assert, so tapping pill A
      // then quickly pill B doesn't snap back to A.
      clearTimeout(faqScrollTimer);
      faqScrollTimer = setTimeout(function () {
        if (Math.abs(section.getBoundingClientRect().top - faqScrollOffset()) > 2) go();
      }, 460);
    }

    // Distance from the viewport top to clear the sticky header + the (now
    // genuinely sticky) category bar, so a jumped-to heading lands just below
    // the bar rather than behind it. Measured so it tracks the responsive
    // sticky offset (80px desktop / 64px mobile) and the bar's own height.
    function faqScrollOffset() {
      var bar = document.querySelector('[data-faq-catnav]');
      if (!bar) return 96;
      var wrapper = bar.closest('.shopify-section--faq-catnav') || bar;
      var stickyTop = parseInt(window.getComputedStyle(wrapper).top, 10);
      if (isNaN(stickyTop)) stickyTop = 0;
      return stickyTop + bar.offsetHeight + 12;
    }

    // On mobile the chip strip is a horizontal scroller. Keep the active pill
    // in view by scrolling the strip itself — never scrollIntoView(), which
    // would also yank the page vertically.
    function ensureChipVisible(chip) {
      if (!chip) return;
      var rail = chip.closest('.faq-catnav__chips');
      if (!rail || rail.scrollWidth <= rail.clientWidth + 1) return; // not scrollable (desktop wraps)
      var chipRect = chip.getBoundingClientRect();
      var railRect = rail.getBoundingClientRect();
      var delta = (chipRect.left + chipRect.width / 2) - (railRect.left + railRect.width / 2);
      var target = Math.max(0, Math.min(rail.scrollLeft + delta, rail.scrollWidth - rail.clientWidth));
      rail.scrollTo({ left: target, behavior: 'smooth' });
    }

    // Scroll-spy: active chip
    var chips = document.querySelectorAll('[data-faq-chip]');
    if (chips.length) {
      var spyCurrent = null;
      var spy = function () {
        var offset = 200;
        var current = null;
        document.querySelectorAll('[data-faq-category]').forEach(function (cat) {
          if (cat.dataset.faqHidden === 'true') return;
          var rect = cat.getBoundingClientRect();
          if (rect.top - offset <= 0) current = cat.getAttribute('data-faq-cat-id');
        });
        if (!current && chips[0]) current = chips[0].getAttribute('data-faq-chip-target');
        if (current === spyCurrent) return; // only act when the active category changes
        spyCurrent = current;
        var active = null;
        chips.forEach(function (c) {
          var on = c.getAttribute('data-faq-chip-target') === current;
          c.classList.toggle('is-active', on);
          if (on) active = c;
        });
        ensureChipVisible(active);
      };
      spy();
      window.addEventListener('scroll', spy, { passive: true });
    }

    // Back-to-top buttons
    document.querySelectorAll('[data-faq-back-to-top]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    });
  }

  function highlightInto(el, text, query) {
    if (!el) return;
    while (el.firstChild) el.removeChild(el.firstChild);
    if (!query) {
      el.appendChild(document.createTextNode(text));
      return;
    }
    var escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    var re = new RegExp('(' + escaped + ')', 'ig');
    var lastIdx = 0;
    var match;
    while ((match = re.exec(text))) {
      if (match.index > lastIdx) {
        el.appendChild(document.createTextNode(text.slice(lastIdx, match.index)));
      }
      var mark = document.createElement('mark');
      mark.className = 'faq-mark';
      mark.textContent = match[0];
      el.appendChild(mark);
      lastIdx = match.index + match[0].length;
      if (match[0].length === 0) re.lastIndex++;
    }
    if (lastIdx < text.length) {
      el.appendChild(document.createTextNode(text.slice(lastIdx)));
    }
  }

  // ---------- Mobile sticky add-to-cart syncs to main add button ----------
  var mobileAdd = document.getElementById('pdp-mobile-add');
  var mainAdd = document.getElementById('pdp-add-btn');
  if (mobileAdd && mainAdd) {
    mobileAdd.addEventListener('click', function () { mainAdd.click(); });
  }

  // ============================================================
  //  Cart page interactions (qty stepper + remove)
  // ============================================================
  // Replaces a section's Shopify wrapper (#shopify-section-<id>) with the
  // HTML returned by the Section Rendering API. Shared by the cart update
  // flow and the upsell add flow.
  function replaceSection(sectionId, html) {
    var wrapper = document.getElementById('shopify-section-' + sectionId);
    if (!wrapper || !html) return false;
    var tmp = document.createElement('div');
    tmp.innerHTML = html.trim();
    var next = tmp.firstElementChild;
    if (!next) return false;

    // The replacement is rendered server-side with its final values already in
    // place, so nothing animates: a brand-new node has no previous state for a
    // CSS transition to run from, and the whole section snaps at once — which
    // reads as a page reload rather than an update. Snapshot the outgoing
    // values first, then run them to the new ones once the swap has happened.
    var before = snapshotCartValues(wrapper);
    wrapper.parentNode.replaceChild(next, wrapper);
    restoreCartAnimations(next, before);
    return true;
  }

  // Values worth animating across a cart section swap, keyed so they can be
  // matched up again in the replacement markup.
  function snapshotCartValues(root) {
    var fill = root.querySelector('[data-fsb-fill]');
    var bar = root.querySelector('[data-free-shipping-bar]');
    var amount = root.querySelector('[data-fsb-amount]');
    var total = root.querySelector('[data-cart-subtotal]');
    var lines = {};
    Array.prototype.forEach.call(root.querySelectorAll('.cart-line[data-line-key]'), function (line) {
      var price = line.querySelector('.cart-line__price-now');
      if (price) lines[line.getAttribute('data-line-key')] = price.innerHTML;
    });
    return {
      width: fill ? fill.style.width : '',
      total: total ? total.innerHTML : '',
      amount: amount ? amount.innerHTML : '',
      // null (no bar before) is distinct from false, so a first render cannot
      // be mistaken for a crossing.
      unlocked: bar ? bar.classList.contains('fsb--unlocked') : null,
      lines: lines
    };
  }

  function restoreCartAnimations(root, before) {
    animateFillFrom(root.querySelector('[data-fsb-fill]'), before.width);
    animateTotalFrom(root.querySelector('[data-cart-subtotal]'), before.total);
    animateTotalFrom(root.querySelector('[data-fsb-amount]'), before.amount);
    // The replacement already carries fsb--unlocked when the cart qualifies, so
    // the crossing can only be detected by comparing against the outgoing node.
    var bar = root.querySelector('[data-free-shipping-bar]');
    if (bar && before.unlocked === false && bar.classList.contains('fsb--unlocked')) {
      fsbCelebrate(bar);
    }
    // Lines are matched by key: a removed line simply has no counterpart and a
    // newly added one has no previous value, so both are left to render plain.
    Array.prototype.forEach.call(root.querySelectorAll('.cart-line[data-line-key]'), function (line) {
      var prev = before.lines[line.getAttribute('data-line-key')];
      if (prev) animateTotalFrom(line.querySelector('.cart-line__price-now'), prev);
    });
  }

  // Rewind a freshly rendered progress fill to the width it had before the
  // swap, then hand it back to CSS to travel to the new one. The forced reflow
  // is load-bearing: without it the browser coalesces both writes into one
  // style recalculation and the transition never runs. Leaving `transition`
  // back at '' restores the stylesheet value, so the reduced-motion rule still
  // wins for anyone who asked for it.
  function animateFillFrom(fill, fromWidth) {
    if (!fill || !fromWidth) return;
    var target = fill.style.width;
    if (!target || target === fromWidth) return;
    fill.style.transition = 'none';
    fill.style.width = fromWidth;
    void fill.offsetWidth;
    fill.style.transition = '';
    fill.style.width = target;
  }

  // Same idea for the cart total, reusing the digit ticker the PDP price
  // already uses. innerHTML rather than textContent throughout: the money
  // format carries currency-app wrapper markup that animatePrice restores.
  function animateTotalFrom(el, fromHtml) {
    if (!el || !fromHtml) return;
    var target = el.innerHTML;
    if (target === fromHtml) return;
    el.innerHTML = fromHtml;
    setPrice(el, target);
  }

  // The cart section lives in a JSON template, so its real section id is
  // dynamic (template--xxxx__main-cart) — read it off the DOM rather than
  // hardcoding 'main-cart', which matches nothing at runtime.
  function cartSectionId() {
    var el = document.querySelector('[data-cart-section]');
    return el ? el.getAttribute('data-section-id') : '';
  }

  // Cache static header refs — used on every quick-add tap and were being
  // re-queried per tap in the hot path. The nav/cart-icon are global and
  // never replaced; the badge IS created/removed dynamically, so it is
  // looked up via getter (cached value goes stale on create/remove).
  var navEl = document.querySelector('.nav');
  var navRightEl = document.querySelector('[data-nav-right]');
  var cartIconEl = document.querySelector('.nav__icon--cart');
  function cartBadgeEl() { return document.querySelector('.cart-count'); }

  function updateCartCount(count) {
    var badges = document.querySelectorAll('.cart-count');
    // Mobile shows the buy CTA while the cart is empty and the cart icon once
    // it is not. The class is server-rendered from cart.item_count, so it goes
    // stale on every in-place add — re-sync it here, the one funnel every
    // cart mutation already passes through. Desktop ignores the class.
    if (navRightEl) { navRightEl.classList.toggle('is-cart-empty', !(count > 0)); }
    // Cart was empty on load, so no badge exists yet — create one inside the
    // header cart link rather than waiting for a page reload.
    if (count > 0 && !badges.length) {
      if (cartIconEl) {
        var span = document.createElement('span');
        span.className = 'cart-count';
        span.textContent = count;
        cartIconEl.appendChild(span);
      }
      return;
    }
    badges.forEach(function (el) {
      if (count > 0) { el.textContent = count; } else { el.remove(); }
    });
  }

  // Shared cart helpers (used by quick-add, cart-upsell, PDP form, kit add,
  // Vext+ tier add, cart line edit, and pageshow). Centralising the fetch
  // boilerplate (headers + non-OK -> parsed throw) means future changes
  // happen in one place.
  function cartPostJson(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (err) { throw err; });
      return r.json();
    });
  }
  function cartAdd(items, sectionIds) {
    var body = { items: items };
    if (sectionIds) { body.sections = sectionIds; }
    return cartPostJson('/cart/add.js', body);
  }
  function cartUpdate(updates, sectionIds) {
    var body = { updates: updates };
    if (sectionIds) { body.sections = sectionIds; }
    return cartPostJson('/cart/update.js', body);
  }
  // Read the cart and reconcile the header badge. /cart/add.js returns only
  // the added lines (no cart-level count), so callers run this after adds.
  function syncCartCount() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        updateCartCount(cart.item_count);
        fsbUpdate(cart.total_price);
        // Hand the cart back so callers can tell success from a failed fetch —
        // the catch below swallows errors and resolves undefined.
        return cart;
      })
      .catch(function () {});
  }
  function cartErrorMessage(err, fallback) {
    return (err && (err.description || err.message)) || fallback || 'Could not add to cart.';
  }

  // Reveal + hold the header for a few seconds (overriding auto-hide on
  // scroll) so the cart stays in view after an in-place add.
  var navPinTimer = null;
  function pinNav() {
    if (!navEl) return;
    navEl.classList.remove('is-nav-hidden');
    navEl.classList.add('is-nav-pinned');
    if (navPinTimer) clearTimeout(navPinTimer);
    navPinTimer = setTimeout(function () {
      navEl.classList.remove('is-nav-pinned');
      navPinTimer = null;
    }, 4000);
  }
  // Pop the cart icon + counter so the change is obvious.
  function bumpCart() {
    if (!cartIconEl) return;
    cartIconEl.classList.remove('is-bumped');
    void cartIconEl.offsetWidth;      // restart the animation on rapid re-adds
    cartIconEl.classList.add('is-bumped');
  }
  // Flip a quick-add + button to the checkmark and replay its pop. Used by
  // both the product-card quick add and the cart-page upsell.
  function popPlus(btn) {
    btn.classList.add('is-added');
    btn.classList.remove('is-pop');
    void btn.offsetWidth;       // restart the keyframe on rapid re-adds
    btn.classList.add('is-pop');
  }

  // ---------- Free shipping progress ----------
  // Markup for the bar lives in snippets/free-shipping-bar.liquid and is
  // rendered twice: permanently in the cart summary, and inside the toast at
  // the bottom of the page. The cart page re-renders its copy server-side on
  // every quantity change, so this code exists for the in-place add paths
  // (product-card quick add, cart upsell) where nothing re-renders.
  var fsbToast = document.querySelector('[data-fsb-toast]');
  var fsbToastTimer = null;

  // Rebuild "<amount> away from free shipping", mirroring what the snippet
  // renders server-side: the figure overlaid on a hidden copy of the widest
  // value so the words after it do not shift as it counts down. Assembled from
  // text nodes rather than innerHTML so merchant copy cannot introduce markup.
  function fsbRenderProgress(label, tmpl, moneyStr, widestStr) {
    var parts = String(tmpl).split('[amount]');

    var ghost = document.createElement('span');
    ghost.className = 'fsb__slot-ghost';
    ghost.setAttribute('aria-hidden', 'true');
    ghost.textContent = widestStr;

    var amountEl = document.createElement('span');
    amountEl.className = 'fsb__amount';
    amountEl.setAttribute('data-fsb-amount', '');
    amountEl.textContent = moneyStr;

    var slot = document.createElement('span');
    slot.className = 'fsb__slot';
    slot.appendChild(ghost);
    slot.appendChild(amountEl);

    label.textContent = '';
    label.appendChild(document.createTextNode(parts.shift() || ''));
    label.appendChild(slot);
    // Any further [amount] tokens stay literal — only the first is the figure.
    label.appendChild(document.createTextNode(parts.join('[amount]')));
    return amountEl;
  }

  // Re-queried every call because the cart section (and the bar inside it) is
  // replaced wholesale by replaceSection().
  function fsbUpdate(totalCents) {
    var bars = document.querySelectorAll('[data-free-shipping-bar]');
    if (!bars.length || typeof totalCents !== 'number') return;
    Array.prototype.forEach.call(bars, function (bar) {
      var threshold = parseInt(bar.getAttribute('data-threshold'), 10);
      if (!threshold || threshold <= 0) return;

      var remaining = threshold - totalCents;
      if (remaining < 0) remaining = 0;
      var pct = Math.round((totalCents * 100) / threshold);
      if (pct > 100) pct = 100;
      if (pct < 0) pct = 0;

      var fill = bar.querySelector('[data-fsb-fill]');
      if (fill) fill.style.width = pct + '%';

      var label = bar.querySelector('[data-fsb-label]');
      if (label) {
        if (remaining > 0) {
          var tmpl = bar.getAttribute('data-progress-text') || '[amount] away from free shipping';
          var sample = bar.getAttribute('data-money-sample') || '';
          var money = formatMoney(remaining, sample);
          var amountEl = label.querySelector('[data-fsb-amount]');
          if (amountEl) {
            // Already showing progress copy — tick just the figure, so the
            // amount counts down like every other price on the site instead of
            // the whole sentence being rewritten under the reader.
            setPrice(amountEl, money);
          } else {
            // Coming back from the unlocked state, so the sentence has to be
            // rebuilt before there is a figure to animate. The widest value is
            // the full threshold — an empty cart — matching what the snippet
            // reserves space for server-side.
            fsbRenderProgress(label, tmpl, money, formatMoney(threshold, sample));
          }
        } else {
          label.textContent = bar.getAttribute('data-success-text') || 'Free shipping unlocked';
        }
      }

      var wasUnlocked = bar.classList.contains('fsb--unlocked');
      if (remaining === 0) {
        bar.classList.add('fsb--unlocked');
        if (!wasUnlocked) fsbCelebrate(bar);
      } else {
        bar.classList.remove('fsb--unlocked');
      }
    });
  }

  // Play the unlock flourish once, on the crossing only. The CSS keys off a
  // one-shot class rather than `fsb--unlocked` alone because the cart page
  // re-renders with that class already applied, which would otherwise replay
  // the animation on every quantity change once the threshold was passed.
  //
  // Cleared on a timer rather than animationend: reduced motion cancels the
  // animation outright, so animationend would never fire and the class would
  // stick, blocking the next genuine crossing.
  function fsbCelebrate(bar) {
    if (!bar) return;
    bar.classList.remove('is-unlocking');
    void bar.offsetWidth;       // restart cleanly if one is already mid-play
    bar.classList.add('is-unlocking');
    if (bar._fsbTimer) clearTimeout(bar._fsbTimer);
    bar._fsbTimer = setTimeout(function () {
      bar.classList.remove('is-unlocking');
      bar._fsbTimer = null;
    }, 700);
  }

  // Brief, non-blocking confirmation after an in-place add. Skipped on the cart
  // page, where the same bar is already on screen permanently — a toast there
  // would just repeat it. The timer restarts on each add so a burst of taps
  // leaves the popup up for 4s after the *last* one, not the first.
  function fsbShowToast() {
    if (!fsbToast || !fsbToast.querySelector('[data-free-shipping-bar]')) return;
    if (document.querySelector('[data-cart-section]')) return;
    fsbToast.classList.add('is-on');
    if (fsbToastTimer) clearTimeout(fsbToastTimer);
    fsbToastTimer = setTimeout(function () {
      fsbToast.classList.remove('is-on');
      fsbToastTimer = null;
    }, 4000);
  }

  // Returning via the back button restores this page from the bfcache with a
  // stale DOM (e.g. an item removed on the cart page still shows in the count
  // here). Re-read the cart on restore so the header badge is truthful.
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) syncCartCount();
  });

  // ---------- Quick add to cart (product cards, e.g. pod cross-sell) ----------
  // The button is never disabled, so fast repeat taps each register and pop.
  // But firing one /cart/add.js per tap means a 10+ tap burst hits the cart
  // endpoint with a storm of concurrent writes, which Shopify throttles/locks
  // (and one comes back as an error). So taps are *coalesced*: the visual
  // confirmation + optimistic count happen per tap, while the actual add is
  // debounced into a single request carrying the summed quantity.
  function qaFlush(btn) {
    if (btn._qaFlush) { clearTimeout(btn._qaFlush); btn._qaFlush = null; }
    btn._qaBurstStart = 0;
    var qty = btn._qaPending || 0;
    btn._qaPending = 0;
    if (qty <= 0) return;
    var variantId = parseInt(btn.getAttribute('data-variant-id'), 10);
    if (!variantId) return;
    cartAdd([{ id: variantId, quantity: qty }])
      .then(function () {
        // More taps arrived during the request — let the next flush reconcile
        // so the count doesn't dip mid-burst.
        // The popup is already open from the tap; this is what fills in the
        // real total behind it.
        if ((btn._qaPending || 0) === 0) syncCartCount();
      })
      .catch(function (err) {
        // Add failed — pull the true count back (optimistic over-counted),
        // drop the confirmed state, and surface the error once.
        syncCartCount();
        if (btn._qaRevert) { clearTimeout(btn._qaRevert); btn._qaRevert = null; }
        btn.classList.remove('is-added', 'is-pop');
        alert(cartErrorMessage(err));
      });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    var variantId = parseInt(btn.getAttribute('data-variant-id'), 10);
    if (!variantId) return;

    // Instant, re-triggerable confirmation on every tap (reflow restarts the
    // pop keyframe so each tap in a burst bounces visibly).
    popPlus(btn);
    pinNav();

    // Optimistic per-tap count + cart pop so the burst feels live; the
    // debounced flush reconciles to the server's true total afterward.
    var badge = cartBadgeEl();
    var shown = badge ? (parseInt(badge.textContent, 10) || 0) : 0;
    updateCartCount(shown + 1);
    bumpCart();
    // Same optimism for the free shipping popup. It already holds the state
    // from before this tap, so it opens showing the truth as last known and the
    // amount then counts down to the new one when the cart read lands. Waiting
    // for that read first cost the debounce plus two round trips before
    // anything appeared, which is a long time to show nothing for what is only
    // a nudge.
    fsbShowToast();

    // Hold the check through a burst; revert ~1.1s after the LAST tap.
    if (btn._qaRevert) clearTimeout(btn._qaRevert);
    btn._qaRevert = setTimeout(function () {
      btn._qaRevert = null;
      if (btn.isConnected) btn.classList.remove('is-added', 'is-pop');
    }, 1100);

    // Accumulate quantity; flush ~350ms after taps settle, but at least every
    // ~900ms during a continuous stream so the cart never lags indefinitely.
    btn._qaPending = (btn._qaPending || 0) + 1;
    if (!btn._qaBurstStart) btn._qaBurstStart = Date.now();
    if (btn._qaFlush) clearTimeout(btn._qaFlush);
    var wait = (Date.now() - btn._qaBurstStart) >= 900 ? 0 : 350;
    btn._qaFlush = setTimeout(function () { qaFlush(btn); }, wait);
  });

  // A free add-on (`_free_addon`, put in with the cabinet by the product page)
  // that no longer comes out free in the cart (the cabinet removed, the
  // discount ended) is taken back out, so it never reaches checkout at full
  // price. The cart section lists them in data-free-addon-stale.
  function dropStaleAddons() {
    var el = document.querySelector('[data-cart-section]');
    var keys = el ? (el.getAttribute('data-free-addon-stale') || '').split(',').filter(Boolean) : [];
    if (!keys.length) return;
    var updates = {};
    keys.forEach(function (key) { updates[key] = 0; });
    var sectionId = cartSectionId();
    cartUpdate(updates, sectionId || undefined)
      .then(function (cart) {
        var html = cart.sections && cart.sections[sectionId];
        if (!replaceSection(sectionId, html)) { window.location.reload(); return; }
        updateCartCount(cart.item_count);
      })
      .catch(function () { /* leave it; the line still shows its price */ });
  }
  dropStaleAddons();

  // Delegate from document (not a cached node): the cart section gets
  // replaced wholesale after updates, and listeners bound to the old node
  // would die with it.
  document.addEventListener('click', function (e) {
    if (!e.target.closest('[data-cart-section]')) return;
    var dec = e.target.closest('[data-qty-decrement]');
    var inc = e.target.closest('[data-qty-increment]');
    var rm = e.target.closest('[data-cart-remove]');
    if (dec) return cartStep(dec, -1);
    if (inc) return cartStep(inc, +1);
    if (rm) {
      var rmLine = rm.closest('.cart-line');
      if (rmLine) return cartUpdateLine(rmLine, 0);
    }
  });
  document.addEventListener('change', function (e) {
    if (!e.target.closest('[data-cart-section]')) return;
    var input = e.target.closest('.cart-qty__input');
    if (input) {
      var qty = Math.max(0, parseInt(input.value, 10) || 0);
      var line = input.closest('.cart-line');
      if (line) cartUpdateLine(line, qty);
    }
  });

  function cartStep(btn, delta) {
    var line = btn.closest('.cart-line');
    if (!line) return;
    var input = line.querySelector('.cart-qty__input');
    if (!input) return;
    var current = parseInt(input.value, 10) || 0;
    var next = Math.max(0, current + delta);
    cartUpdateLine(line, next);
  }

  // Updates a parent line and cascades the change to any hidden kit children
  // (line items with `_kit_parent` private property) via /cart/update.js.
  function cartUpdateLine(lineEl, newQty) {
    var key = lineEl.getAttribute('data-line-key');
    if (!key) return;
    var origQty = parseInt(lineEl.getAttribute('data-line-orig-qty'), 10);
    if (!origQty || origQty < 1) origQty = 1;

    var updates = {};
    updates[key] = newQty;

    var kidKeysAttr = lineEl.getAttribute('data-kit-child-keys') || '';
    var kidQtysAttr = lineEl.getAttribute('data-kit-child-qtys') || '';
    if (kidKeysAttr) {
      var kidKeys = kidKeysAttr.split('|');
      var kidQtys = kidQtysAttr.split('|');
      var ratio = newQty === 0 ? 0 : (newQty / origQty);
      for (var i = 0; i < kidKeys.length; i++) {
        if (!kidKeys[i]) continue;
        var origKidQty = parseInt(kidQtys[i], 10) || 1;
        var newKidQty = newQty === 0 ? 0 : Math.max(1, Math.round(origKidQty * ratio));
        updates[kidKeys[i]] = newKidQty;
      }
    }

    var sectionId = cartSectionId();
    cartUpdate(updates, sectionId || undefined)
      .then(function (cart) {
        // Swap the cart section in place instead of reloading the page —
        // a reload also re-runs the upsell fetch, causing a visible
        // blank-then-populated flicker on every quantity change.
        var html = cart.sections && cart.sections[sectionId];
        if (!replaceSection(sectionId, html)) { window.location.reload(); return; }
        updateCartCount(cart.item_count);
        dropStaleAddons();
        // Pop the header badge the same way an add does, so a quantity change
        // registers as a change even when the summary is scrolled out of view.
        bumpCart();
        if (cart.item_count === 0) {
          // No items left — "goes well with your cart" no longer applies.
          // The schema class lands on the section wrapper itself.
          var upsell = document.querySelector('.shopify-section--cart-upsell');
          if (upsell) upsell.remove();
        }
      })
      .catch(function (err) {
        alert(cartErrorMessage(err, 'Could not update cart.'));
      });
  }

  // ============================================================
  //  Membership page: gallery thumbs + term selector + add-to-cart
  // ============================================================
  var memBuyRoot = document.querySelector('[data-mem-buy-root]');
  if (memBuyRoot) initMembershipBuy();

  function initMembershipBuy() {
    // Gallery thumbs
    var memImg = document.getElementById('mem-gallery-img');
    var memCap = document.getElementById('mem-gallery-caption');
    var memThumbs = document.querySelectorAll('[data-mem-gallery] .thumb');
    if (memImg && memThumbs.length) {
      memThumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          memThumbs.forEach(function (t) { t.classList.remove('is-active'); });
          thumb.classList.add('is-active');
          var src = thumb.getAttribute('data-full-src');
          var caption = thumb.getAttribute('data-caption');
          var alt = thumb.getAttribute('data-alt');
          if (src) {
            memImg.removeAttribute('srcset');
            memImg.src = src;
          }
          if (alt) memImg.alt = alt;
          if (memCap && caption) memCap.textContent = caption;
        });
      });
    }

    // Term chips
    var termChips = document.querySelectorAll('[data-mem-term]');
    var memPrice = document.getElementById('mem-price');
    var memSave = document.getElementById('mem-save');
    var memTermName = document.getElementById('mem-term-name');

    termChips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        termChips.forEach(function (c) {
          c.classList.remove('is-active');
          c.setAttribute('aria-checked', 'false');
        });
        chip.classList.add('is-active');
        chip.setAttribute('aria-checked', 'true');

        var p = chip.getAttribute('data-term-price') || '';
        var s = chip.getAttribute('data-term-save') || '';
        var n = chip.getAttribute('data-term-name') || '';
        setPrice(memPrice, p);
        if (memTermName) memTermName.textContent = n;
        if (memSave) {
          if (s) {
            memSave.textContent = s;
            memSave.style.display = '';
          } else {
            memSave.style.display = 'none';
          }
        }
      });
    });

    // Add to cart
    var memAdd = document.getElementById('mem-add-btn');
    if (memAdd) {
      memAdd.addEventListener('click', function () {
        var active = document.querySelector('[data-mem-term].is-active');
        if (!active) return;
        var variantId = parseInt(active.getAttribute('data-term-variant'), 10);
        if (!variantId) {
          alert('Membership product not configured. Set the variant ID in the theme editor.');
          return;
        }
        memAdd.disabled = true;
        cartAdd([{ id: variantId, quantity: 1 }])
          .then(function () { window.location.href = '/cart'; })
          .catch(function (err) {
            alert(cartErrorMessage(err, 'Could not add Vext+ to cart.'));
            memAdd.disabled = false;
          });
      });
    }
  }

  // ============================================================
  //  Pods page: pack filter chips
  // ============================================================
  var packFilter = document.querySelector('[data-pack-filter]');
  var packGrid = document.querySelector('[data-pack-grid]');
  if (packFilter && packGrid) initPackFilter(packFilter, packGrid);

  function initPackFilter(filterRoot, grid) {
    var chips = filterRoot.querySelectorAll('[data-pack-filter-id]');
    var cards = grid.querySelectorAll('[data-pack-cat]');

    function applyFilter(id) {
      var target = (id || 'all').toLowerCase().trim();
      chips.forEach(function (c) {
        var match = c.getAttribute('data-pack-filter-id') === target;
        c.classList.toggle('is-active', match);
        c.setAttribute('aria-selected', match ? 'true' : 'false');
      });
      cards.forEach(function (card) {
        var cat = (card.getAttribute('data-pack-cat') || '').toLowerCase().trim();
        var show = target === 'all' || cat === target;
        card.hidden = !show;
      });
    }

    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        var id = chip.getAttribute('data-pack-filter-id');
        applyFilter(id);
        if (id) history.replaceState(null, '', '#all-packs' + (id === 'all' ? '' : '=' + id));
      });
    });

    // Honor URL hash like #all-packs=herbs on initial load. The hash
    // contains a value (=herbs) so the browser doesn't treat it as an
    // anchor and skips its native scroll-to-id — we scroll to the
    // filter section explicitly so deep links from the homepage cards
    // land the user on the filtered content, not at the top of the
    // page. We don't scroll on chip click (user is already in place).
    var hash = window.location.hash || '';
    var match = hash.match(/all-packs=([\w-]+)/);
    if (match) {
      applyFilter(match[1]);
      var sec = document.getElementById('all-packs');
      if (sec) {
        requestAnimationFrame(function () {
          sec.scrollIntoView({ block: 'start' });
        });
      }
    }
  }

  // Mobile menu drawer
  var menuBurger = document.querySelector('.nav__burger');
  var menuDrawer = document.getElementById('nav-drawer');
  var menuCloseBtn = menuDrawer && menuDrawer.querySelector('.nav__drawer-close');
  var menuOpenedBy = null;
  function openMenu() {
    if (document.body.classList.contains('menu-open')) return;
    menuOpenedBy = document.activeElement;
    document.body.classList.add('menu-open');
    if (menuBurger) menuBurger.setAttribute('aria-expanded', 'true');
    if (menuDrawer) menuDrawer.setAttribute('aria-hidden', 'false');
    if (menuCloseBtn) requestAnimationFrame(function () { menuCloseBtn.focus(); });
  }
  function closeMenu() {
    if (!document.body.classList.contains('menu-open')) return;
    document.body.classList.remove('menu-open');
    if (menuBurger) menuBurger.setAttribute('aria-expanded', 'false');
    if (menuDrawer) menuDrawer.setAttribute('aria-hidden', 'true');
    if (menuOpenedBy && menuOpenedBy.focus && document.contains(menuOpenedBy)) menuOpenedBy.focus();
    else if (menuBurger) menuBurger.focus();
  }
  if (menuBurger || menuDrawer) {
    document.addEventListener('click', function (e) {
      if (!e.target.closest) return;
      var hit = e.target.closest('[data-menu-toggle],[data-menu-close]');
      if (!hit) return;
      if (hit.hasAttribute('data-menu-toggle')) {
        e.preventDefault();
        if (document.body.classList.contains('menu-open')) closeMenu();
        else openMenu();
      } else {
        closeMenu();
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && document.body.classList.contains('menu-open')) closeMenu();
    });
  }

  // ---------- Drawer nav groups (mobile accordion) ----------
  // Same shape as the spec-sheet accordion: toggle is-open on the row and let
  // CSS animate grid-template-rows. Delegated from the drawer so it survives
  // section re-renders in the theme editor.
  (function () {
    var drawer = document.getElementById('nav-drawer');
    if (!drawer) return;
    drawer.addEventListener('click', function (e) {
      if (!e.target.closest) return;
      var btn = e.target.closest('[data-drawer-group-toggle]');
      if (!btn) return;
      var group = btn.closest('[data-drawer-group]');
      if (!group) return;
      var open = group.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  })();

  // ---------- Desktop nav groups (dropdowns) ----------
  // CSS handles hover and :focus-within on its own; this only adds what CSS
  // cannot: click/tap to open (a hover-only menu is unusable on a touch device
  // that reports no hover), Escape to close, and click-outside to dismiss.
  // The panel stays in the DOM either way, so nothing here is load-bearing for
  // keyboard users if the script fails.
  (function () {
    var groups = document.querySelectorAll('[data-nav-group]');
    if (!groups.length) return;

    function close(group) {
      group.classList.remove('is-open');
      var t = group.querySelector('[data-nav-group-toggle]');
      if (t) t.setAttribute('aria-expanded', 'false');
    }
    // CSS opens the panel on :hover, which would keep it visible after an
    // Escape while the pointer still rests on the toggle — the key would look
    // dead. This marks the group so a rule can out-specify :hover, and clears
    // on pointer leave so hover behaves normally next time.
    function dismiss(group) {
      close(group);
      group.classList.add('is-dismissed');
    }
    function closeAll(except) {
      Array.prototype.forEach.call(groups, function (g) { if (g !== except) close(g); });
    }

    Array.prototype.forEach.call(groups, function (group) {
      var toggle = group.querySelector('[data-nav-group-toggle]');
      if (!toggle) return;
      group.addEventListener('mouseleave', function () { group.classList.remove('is-dismissed'); });
      toggle.addEventListener('click', function (e) {
        e.preventDefault();
        group.classList.remove('is-dismissed');
        var isOpen = group.classList.contains('is-open');
        closeAll(group);
        if (isOpen) close(group);
        else {
          group.classList.add('is-open');
          toggle.setAttribute('aria-expanded', 'true');
        }
      });
    });

    document.addEventListener('click', function (e) {
      if (!e.target.closest || !e.target.closest('[data-nav-group]')) closeAll(null);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      Array.prototype.forEach.call(groups, function (g) {
        var t = g.querySelector('[data-nav-group-toggle]');
        var openByClick = g.classList.contains('is-open');
        var openByHover = g.matches(':hover');
        if (!openByClick && !openByHover) return;
        dismiss(g);
        if (openByClick && t) t.focus();
      });
    });
  })();

  // ---------- Auto-hide nav on scroll ----------
  // Hide the nav when scrolling down, reveal it when scrolling up. Always
  // shown near the top and while the mobile menu is open. The transform
  // lives on .nav (not the sticky wrapper) so it doesn't become a
  // containing block for the position:fixed drawer/scrim that are its
  // siblings inside the header.
  (function () {
    var nav = navEl;
    if (!nav) return;
    var lastY = window.scrollY || 0;
    var ticking = false;
    var REVEAL_AT = 80;  // px from top below which the nav always shows
    var DEADZONE = 6;    // ignore sub-pixel jitter
    function onFrame() {
      ticking = false;
      var y = window.scrollY || 0;
      // menu-open and the post-add pin both force the nav to stay visible.
      if (document.body.classList.contains('menu-open') || nav.classList.contains('is-nav-pinned')) {
        nav.classList.remove('is-nav-hidden');
        document.body.classList.remove('nav-hidden');
        lastY = y;
        return;
      }
      if (y <= REVEAL_AT) {
        nav.classList.remove('is-nav-hidden');
      } else if (y > lastY + DEADZONE) {
        nav.classList.add('is-nav-hidden');
      } else if (y < lastY - DEADZONE) {
        nav.classList.remove('is-nav-hidden');
      }
      // Mirror the hidden state onto <body> so position:sticky bars (e.g. the
      // FAQ category nav) can close the empty header-height gap when the nav
      // auto-hides on scroll-down.
      document.body.classList.toggle('nav-hidden', nav.classList.contains('is-nav-hidden'));
      lastY = y;
    }
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(onFrame);
    }, { passive: true });
  })();

  // ---------- Prefetch on hover / touch ----------
  // When the user hovers (desktop) or starts touching (mobile) a same-origin
  // link, fire <link rel="prefetch"> for that URL. By the time they actually
  // click, the destination is in the browser cache and the navigation feels
  // instant. Respects Save-Data and slow connections.
  (function () {
    var conn = navigator.connection;
    if (conn && (conn.saveData || /2g/.test(conn.effectiveType || ''))) return;
    var prefetched = Object.create(null);
    function prefetch(url) {
      if (prefetched[url]) return;
      prefetched[url] = true;
      var link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = url;
      link.as = 'document';
      document.head.appendChild(link);
    }
    function maybePrefetch(target) {
      var a = target && target.closest && target.closest('a[href]');
      if (!a) return;
      var href = a.getAttribute('href');
      if (!href || href.charAt(0) === '#') return;
      // Skip non-http(s), e.g. mailto:, tel:.
      if (/^(mailto|tel|javascript|data):/i.test(href)) return;
      var url;
      try { url = new URL(a.href, location.href); } catch (e) { return; }
      if (url.origin !== location.origin) return;
      if (url.pathname === location.pathname && !url.search) return;
      prefetch(url.href);
    }
    document.addEventListener('mouseover', function (e) { maybePrefetch(e.target); }, { passive: true });
    document.addEventListener('touchstart', function (e) { maybePrefetch(e.target); }, { passive: true });
    document.addEventListener('focusin', function (e) { maybePrefetch(e.target); });
  })();

  // ============================================================
  //  Cart upsell: fetch recommendations and add-without-reload
  // ============================================================
  // Two responsibilities:
  //   1. On cart-page load the upsell section is rendered as an empty
  //      placeholder ([data-cart-upsell]) carrying the seed product id of
  //      the first cart line. We call /recommendations/products to get the
  //      populated section HTML and swap it in.
  //   2. Each upsell card has a [data-cart-upsell-add] button that posts
  //      to /cart/add.js with the cart section's dynamic id, so we can
  //      refresh the cart line list in place rather than reloading.
  (function () {
    function fetchUpsell(holder) {
      var seedId = holder.getAttribute('data-seed-id');
      var sectionId = holder.getAttribute('data-section-id');
      var limit = parseInt(holder.getAttribute('data-limit'), 10) || 4;
      if (!seedId || !sectionId) return;

      // "complementary" returns nothing unless the merchant has configured
      // frequently-bought-together rules in Search & Discovery — fall back
      // to "related" (always algorithm-backed) before giving up.
      var intents = [holder.getAttribute('data-intent') || 'related'];
      if (intents[0] !== 'related') intents.push('related');

      function attempt(i) {
        if (i >= intents.length) {
          // Genuinely no recommendations for any intent — drop the section.
          var wrapper = document.getElementById('shopify-section-' + sectionId);
          if (wrapper) wrapper.remove();
          return;
        }
        var url = '/recommendations/products'
          + '?section_id=' + encodeURIComponent(sectionId)
          + '&product_id=' + encodeURIComponent(seedId)
          + '&limit=' + limit
          + '&intent=' + encodeURIComponent(intents[i]);

        fetch(url, { headers: { 'Accept': 'text/html' } })
          .then(function (r) { return r.ok ? r.text() : ''; })
          .then(function (html) {
            // The Section Rendering API returns the full <div id="shopify-
            // section-X">…</div> wrapper. Swap our placeholder for it when
            // it actually contains cards; otherwise try the next intent.
            var wrapper = document.getElementById('shopify-section-' + sectionId);
            if (!wrapper) return;
            if (html) {
              var tmp = document.createElement('div');
              tmp.innerHTML = html.trim();
              var replacement = tmp.firstElementChild;
              if (replacement && replacement.id === wrapper.id && replacement.querySelector('.cart-upsell')) {
                wrapper.parentNode.replaceChild(replacement, wrapper);
                return;
              }
            }
            attempt(i + 1);
          })
          .catch(function () {
            // Transient network failure: keep the hidden placeholder so the
            // upsell can come back on the next pageview, rather than
            // permanently removing it for the session.
          });
      }
      attempt(0);
    }

    var holder = document.querySelector('[data-cart-upsell]');
    if (holder) fetchUpsell(holder);

    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-cart-upsell-add]');
      if (!btn || btn.disabled) return;
      e.preventDefault();
      var variantId = parseInt(btn.getAttribute('data-variant-id'), 10);
      if (!variantId) return;

      // Same + -> checkmark feedback as the product-card quick add. Disable
      // during the request so rapid taps don't stack concurrent section swaps.
      popPlus(btn);
      btn.disabled = true;

      // The cart section's id is dynamic (JSON template), so resolve it from
      // the DOM — hardcoding 'main-cart' matches nothing at runtime.
      var cartSecId = cartSectionId();
      cartAdd([{ id: variantId, quantity: 1 }], cartSecId || undefined)
        .then(function (data) {
          var html = data && data.sections && data.sections[cartSecId];
          if (!replaceSection(cartSecId, html)) {
            // No section to swap (or render failed) — hard refresh keeps
            // the cart view truthful.
            window.location.reload();
            return;
          }
          // The cart section was swapped wholesale — let motion.js re-tag
          // and re-observe the new nodes (and stop holding refs to old ones).
          if (window.__vextMotion && typeof window.__vextMotion.rescan === 'function') {
            window.__vextMotion.rescan();
          }
          syncCartCount();
          setTimeout(function () {
            if (!btn.isConnected) return;
            btn.disabled = false;
            btn.classList.remove('is-added', 'is-pop');
          }, 1400);
        })
        .catch(function (err) {
          btn.disabled = false;
          btn.classList.remove('is-added', 'is-pop');
          alert(cartErrorMessage(err));
        });
    });
  })();
})();
